import { useEffect, useRef, useState } from 'react';

import { trackCheckoutEvent } from '../utils/analytics';

export type KlarnaCheckoutProps = {
  htmlMarkup: string;
};

const KlarnaCheckout = ({ htmlMarkup }: KlarnaCheckoutProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isRendered, setIsRendered] = useState(false);
  const [prevHtmlMarkup, setPrevHtmlMarkup] = useState(htmlMarkup);

  if (htmlMarkup !== prevHtmlMarkup) {
    setPrevHtmlMarkup(htmlMarkup);
    setIsRendered(false);
  }

  const injectScripts = (container: HTMLDivElement) => {
    const scripts = container.querySelectorAll('script');
    scripts.forEach((script) => {
      const newScript = document.createElement('script');
      Array.from(script.attributes).forEach((attr) => {
        newScript.setAttribute(attr.name, attr.value);
      });
      newScript.text = script.text;

      script.parentNode.appendChild(newScript);
      script.parentNode.removeChild(script);
    });
  };

  useEffect(() => {
    const container = containerRef.current;
    let cancelled = false;

    if (!htmlMarkup) {
      if (container) {
        container.innerHTML = '';
      }
      return;
    }

    if (!container) {
      return;
    }

    try {
      container.innerHTML = '';

      container.innerHTML = htmlMarkup;

      injectScripts(container);

      queueMicrotask(() => {
        if (cancelled) {
          return;
        }
        setIsRendered(true);

        trackCheckoutEvent('checkout_initiated', {
          step: 'payment_selection',
          step_index: 2
        });
      });
    } catch (error) {
      trackCheckoutEvent('checkout_failed', {
        step: 'payment_selection',
        step_index: 2,
        error: 'Failed to render Kustom checkout'
      });
      console.error('Error rendering Kustom checkout:', error);
      queueMicrotask(() => {
        if (cancelled) {
          return;
        }
        setIsRendered(false);
      });
    }

    return () => {
      cancelled = true;
      container.innerHTML = '';
    };
  }, [htmlMarkup]);

  return (
    <div
      ref={containerRef}
      className="klarna-checkout-container"
      style={{
        width: '100%',
        minHeight: '500px',
        padding: '16px'
      }}
      aria-busy={Boolean(htmlMarkup) && !isRendered}
    />
  );
};

KlarnaCheckout.displayName = 'KustomCheckout';

export default KlarnaCheckout;
